import { GraphQLErrors } from '@atlassian/jira-business-graphql-errors/src/index.tsx';
import { UNSAFE_noExposureExp, expVal } from '@atlassian/jira-feature-experiments';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	BOARD_ISSUES_JQL,
	CREATED_THIS_WEEK_JQL,
	DONE_THIS_WEEK_JQL,
	DUE_THIS_WEEK_JQL,
	OPEN_ISSUES_JQL,
	PAGED_DATA_PAGE_SIZE,
	UPDATED_THIS_WEEK_JQL,
} from '../constants.tsx';
import { SUMMARY_PAGE_DATA_QUERY_OLD, SUMMARY_PAGE_DATA_QUERY } from './gql.tsx';
import type { Response, SummaryPageDataType } from './types.tsx';
import { transformData } from './utils.tsx';

const QUERY_NAME = 'SummaryPageData';

type Props = {
	cloudId: string;
	jqlContext: string;
	projectKey: string;
};

export const fetchSummaryPageData = async ({
	cloudId,
	jqlContext,
	projectKey,
}: Props): Promise<SummaryPageDataType> => {
	// We must use UNSAFE_noExposureExp to check if experiment is enabled without actually firing exposure events
	// eslint-disable-next-line jira/ff/unsafe-no-exposure
	const [expInlineStatusConfig] = UNSAFE_noExposureExp('business_summary_page_inline_status');
	const isExperimentSummaryPageInlineStatusEnabled = expInlineStatusConfig.get(
		'isInlineStatusChangeEnabled',
		false,
	);

	const response = await performPostRequest<Response>(`/rest/gira/1/?operation=${QUERY_NAME}`, {
		body: JSON.stringify({
			query: expVal('jwm-summary-page-migration', 'isEnabled', false)
				? SUMMARY_PAGE_DATA_QUERY
				: SUMMARY_PAGE_DATA_QUERY_OLD,
			variables: {
				cloudId,
				jqlContext,
				// unused variable for the new GQL query
				projectKey: expVal('jwm-summary-page-migration', 'isEnabled', false)
					? undefined
					: projectKey,
				doneThisWeekJql: `${jqlContext} AND ${DONE_THIS_WEEK_JQL}`,
				updatedThisWeekJql: `${jqlContext} AND ${UPDATED_THIS_WEEK_JQL}`,
				createdThisWeekJql: `${jqlContext} AND ${CREATED_THIS_WEEK_JQL}`,
				dueThisWeekJql: `${jqlContext} AND ${DUE_THIS_WEEK_JQL}`,
				issuesByStatusJql: `${jqlContext} AND ${BOARD_ISSUES_JQL}`,
				issuesByPriorityJql: `${jqlContext} AND ${OPEN_ISSUES_JQL}`,
				issuesByAssigneeJql: `${jqlContext} AND ${OPEN_ISSUES_JQL}`,
				issuesByTypeJql: `${jqlContext} AND ${OPEN_ISSUES_JQL}`,
				pagedDataPageSize: PAGED_DATA_PAGE_SIZE,
				// unused variable for the new GQL query
				isWorkflowsCountEnabled: expVal('jwm-summary-page-migration', 'isEnabled', false)
					? undefined
					: isExperimentSummaryPageInlineStatusEnabled,
			},
		}),
	});

	if (response.errors != null) {
		throw new GraphQLErrors(response.errors);
	}

	if (response.data == null) {
		throw new Error('GraphQL response has no data');
	}

	return transformData(response.data);
};
